// Page Me — beeps, synthesised with WebAudio. No sample files in the web app.
//
// Browsers (and iOS WebViews) refuse to start audio before a user gesture, so
// the context is created/resumed on the first pointer or key press. A beep
// requested before that is held (the newest one only) and played on the tap
// that unlocks audio — "the first beep plays on the first user tap".

import { ref } from 'vue';
import { haptic } from '../core/util.js';

export const BEEP_KINDS = ['classic', 'chirp', 'twoway', 'ping'];

/** True once a gesture has unlocked audio. The web-only "tap to let your pager beep" line reads this. */
export const audioUnlocked = ref(false);

let ctx = null;
let pending = null;

function context() {
  if (ctx) return ctx;
  try {
    const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!AC) return null;
    ctx = new AC();
  } catch { ctx = null; }
  return ctx;
}

function unlock() {
  const ac = context();
  if (!ac) return;
  const done = () => {
    audioUnlocked.value = ac.state === 'running';
    if (audioUnlocked.value) {
      removeListeners();
      if (pending) { const k = pending; pending = null; synth(k); }
    }
  };
  try {
    // A one-sample silent buffer: the gesture-bound "play something" iOS wants.
    const buf = ac.createBuffer(1, 1, 22050);
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(ac.destination);
    src.start(0);
  } catch { /* ignore */ }
  if (ac.state === 'suspended') ac.resume().then(done, () => {});
  else done();
}

const GESTURES = ['pointerdown', 'keydown', 'touchend'];
function removeListeners() {
  for (const g of GESTURES) removeEventListener(g, unlock, true);
}
if (typeof addEventListener === 'function' && typeof window !== 'undefined') {
  for (const g of GESTURES) addEventListener(g, unlock, { capture: true, passive: true });
}

// ── synthesis ────────────────────────────────────────────────────────────────

/** One enveloped oscillator note. `t` is seconds from now. */
function note(ac, { t = 0, dur = 0.09, freq = 2800, to = null, type = 'square', gain = 0.06 }) {
  const start = ac.currentTime + t;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (to) osc.frequency.exponentialRampToValueAtTime(to, start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.005);
  g.gain.setValueAtTime(gain, start + Math.max(0.006, dur - 0.02));
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

const PATTERNS = {
  // Triple ~2.8 kHz square beep: the belt-clip classic.
  classic: [
    { t: 0.00, dur: 0.09, freq: 2800 },
    { t: 0.15, dur: 0.09, freq: 2800 },
    { t: 0.30, dur: 0.09, freq: 2800 },
  ],
  // Two rising chirps.
  chirp: [
    { t: 0.00, dur: 0.12, freq: 1600, to: 3200, type: 'sine', gain: 0.08 },
    { t: 0.18, dur: 0.12, freq: 1600, to: 3200, type: 'sine', gain: 0.08 },
  ],
  // Two-tone, low then high, twice.
  twoway: [
    { t: 0.00, dur: 0.10, freq: 1400, type: 'triangle', gain: 0.09 },
    { t: 0.11, dur: 0.10, freq: 2100, type: 'triangle', gain: 0.09 },
    { t: 0.30, dur: 0.10, freq: 1400, type: 'triangle', gain: 0.09 },
    { t: 0.41, dur: 0.10, freq: 2100, type: 'triangle', gain: 0.09 },
  ],
  // A soft sine ping with a long tail.
  ping: [
    { t: 0.00, dur: 0.45, freq: 1320, type: 'sine', gain: 0.1 },
    { t: 0.00, dur: 0.30, freq: 2640, type: 'sine', gain: 0.02 },
  ],
  // UI extras for the device: a key blip and the wrap-around double beep.
  blip: [{ t: 0, dur: 0.035, freq: 2400, gain: 0.03 }],
  double: [
    { t: 0.00, dur: 0.06, freq: 2600, gain: 0.05 },
    { t: 0.10, dur: 0.06, freq: 2600, gain: 0.05 },
  ],
};

function synth(kind) {
  const ac = context();
  if (!ac) return false;
  const pattern = PATTERNS[kind] ?? PATTERNS.classic;
  try {
    for (const n of pattern) note(ac, n);
    return true;
  } catch { return false; }
}

/**
 * Play a beep now, or on the first tap if audio is still locked.
 * @param {'classic'|'chirp'|'twoway'|'ping'|'blip'|'double'} kind
 */
export function playBeep(kind = 'classic') {
  const ac = context();
  if (!ac) return false;
  if (ac.state !== 'running') {
    ac.resume?.().catch(() => {});
    if (!audioUnlocked.value) { if (kind !== 'blip') pending = kind; return false; }
  }
  return synth(kind);
}

// ── alerts ───────────────────────────────────────────────────────────────────

// The store tells us the alert mode and the device's beep, so this module does
// not import the store (which imports it).
let source = () => ({ mode: 'beep', beep: 'classic' });
export function setAlertSource(fn) { if (typeof fn === 'function') source = fn; }

/** A page arrived: beep + haptic, vibrate only, or nothing — per ALERT. */
export function alertFor(page) {
  const { mode, beep } = source(page) ?? {};
  if (mode === 'silent') return;
  if (mode === 'vibrate') {
    haptic('heavy');
    try { navigator.vibrate?.([200, 100, 200]); } catch { /* ignore */ }
    return;
  }
  haptic('pop');
  playBeep(beep || 'classic');
}
